import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { FaEdit, FaTrash, FaSave, FaTimes, FaThList, FaTh, FaSearch, FaArchive, FaCog, FaUpload } from 'react-icons/fa';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import CreateTreatment from './CreateTreatment';
import DataTable from 'react-data-table-component-with-filter';
import InternalHeader from '../InternalHeader';
import Sidebar from '../Sidebar';
import './Treatment.css';
import CustomPagination from '../CustomPagination/CustomPagination';
import FilterTreatments from "../Filter/FilterTreatments"; // Import the new FilterTreatments component
import Actions from "../Actions/Actions";
import { format } from 'date-fns';

const Treatments = () => {
    const navigate = useNavigate();
    const [filterText, setFilterText] = useState('');
    const [treatments, setTreatments] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedTreatmentId, setSelectedTreatmentId] = useState(null);
    const [isSidebarVisible, setIsSidebarVisible] = useState(true);
    const [editedTreatment, setEditedTreatment] = useState({
        Legend: "",
        Treatmentname: "",
        Cost: ""
    });
    const [showModal, setShowModal] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [treatmentToDelete, setTreatmentToDelete] = useState(null);
    const totalRecords = treatments.length;
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewOption, setViewOption] = useState(''); // State for filtering by status or archived
    const [selectedTreatment, setSelectedTreatment] = useState([]);
    const [isActionsDropdownOpen, setIsActionsDropdownOpen] = useState(false);
    const [logs, setLogs] = useState([]);

    const logChange = (action, details) => {
        setLogs(prevLogs => [...prevLogs, { timestamp: new Date().toLocaleString(), action, details }]);
    };

    const handleActionsToggle = () => {
        setIsActionsDropdownOpen(!isActionsDropdownOpen);
    };

    const handleDeleteSelected = async () => {
        if (selectedTreatment.length === 0) {
            alert('No treatments selected for archiving!');
            return;
        }

        const confirmArchive = window.confirm('Are you sure you want to archive the selected treatments?');
        if (!confirmArchive) {
            return;
        }

        try {
            const response = await fetch('https://api.hgcdental.clinic/api/treatments/archive', {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    treatmentIds: selectedTreatment.map(treatment => treatment._id)
                })
            });

            const result = await response.json();

            if (response.ok) {
                // Update the local state to reflect the archived status
                setTreatments(prevTreatments =>
                    prevTreatments.map(treatment =>
                        selectedTreatment.some(selected => selected._id === treatment._id)
                            ? { ...treatment, isArchived: true }
                            : treatment
                    )
                );
                setSelectedTreatment([]); // Clear selected treatments
                alert(result.message);
            } else {
                alert(result.message || 'Failed to archive treatments');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('An error occurred while archiving treatments');
        }
    };

    const handleUnDeleteSelected = async () => {
        if (selectedTreatment.length === 0) {
            alert('No treatments selected for unarchiving!');
            return;
        }

        const confirmArchive = window.confirm('Are you sure you want to unarchive the selected treatments?');
        if (!confirmArchive) {
            return;
        }

        try {
            const response = await fetch('https://api.hgcdental.clinic/api/treatments/unarchive', {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    treatmentIds: selectedTreatment.map(treatment => treatment._id)
                })
            });

            const result = await response.json();

            if (response.ok) {
                // Update the local state to reflect the archived status
                setTreatments(prevTreatments =>
                    prevTreatments.map(treatment =>
                        selectedTreatment.some(selected => selected._id === treatment._id)
                            ? { ...treatment, isArchived: false }
                            : treatment
                    )
                );
                setSelectedTreatment([]); // Clear selected treatments
                alert(result.message);
            } else {
                alert(result.message || 'Failed to unarchive treatments');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('An error occurred while unarchiving treatments');
        }
    };

    const handleFilterChange = (event) => {
        setFilterText(event.target.value);
    };

    const toggleSidebar = () => {
        setIsSidebarVisible(!isSidebarVisible);
    };

    const addTreatment = (newTreatment) => {
        setTreatments(prevTreatments => [...prevTreatments, newTreatment]);
    };

    useEffect(() => {
        const fetchTreatments = async () => {
            const response = await fetch('https://api.hgcdental.clinic/api/treatments');
            const json = await response.json();

            if (response.ok) {
                setTreatments(json);
                logChange('Fetched', `Loaded ${json.length} treatments.`);
            }
        };

        fetchTreatments();
    }, []);

    const handleDeleteClick = (treatmentId) => {
        const treatment = treatments.find(t => t._id === treatmentId);
        setTreatmentToDelete(treatment);
        setShowDeleteConfirmation(true);
    };

    const confirmDelete = async () => {
        try {
            const deleteResponse = await fetch(`https://api.hgcdental.clinic/api/treatments/${treatmentToDelete._id}`, {
                method: 'DELETE'
            });

            if (deleteResponse.ok) {
                setTreatments(prevTreatments => prevTreatments.filter(t => t._id !== treatmentToDelete._id));
                setShowDeleteConfirmation(false);
            } else {
                alert('Error deleting treatment');
            }
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const cancelDelete = () => {
        setShowDeleteConfirmation(false);
        setTreatmentToDelete(null);
    };

    const handleEditClick = (treatment) => {
        setSelectedTreatmentId(treatment._id);
        setEditedTreatment({
            Legend: treatment.Legend,
            Treatmentname: treatment.Treatmentname,
            Cost: treatment.Cost
        });
        setIsEditing(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditedTreatment(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSaveClick = async () => {
        try {
            const response = await fetch(`https://api.hgcdental.clinic/api/treatments/${selectedTreatmentId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(editedTreatment)
            });

            const updatedTreatment = await response.json();

            if (response.ok) {
                setTreatments(prevTreatments =>
                    prevTreatments.map(treatment =>
                        treatment._id === selectedTreatmentId ? updatedTreatment : treatment
                    )
                );
                setIsEditing(false);
                setSelectedTreatmentId(null);
            } else {
                alert('Error updating treatment');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('An error occurred while updating the treatment');
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setSelectedTreatmentId(null);
        setEditedTreatment({ Legend: "", Treatmentname: "", Cost: "" });
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    // Function to handle row click
    const handleRowClick = (row) => {
        navigate(`/treatment-details/${row._id}`); // Navigate to the treatment details page
    };

    const columns = [
        {
            name: 'Legend',
            selector: row => row.Legend || '',
            sortable: true,
            filterable: true,
            cell: row => isEditing && selectedTreatmentId === row._id ? (
                <input
                    type="text"
                    name="Legend"
                    value={editedTreatment.Legend}
                    onChange={handleInputChange}
                />
            ) : (
                row.Legend
            ),
        },
        {
            name: 'Treatment Name',
            selector: row => row.Treatmentname || '',
            sortable: true,
            filterable: true,
            cell: row => isEditing && selectedTreatmentId === row._id ? (
                <input
                    type="text"
                    name="Treatmentname"
                    value={editedTreatment.Treatmentname}
                    onChange={handleInputChange}
                />
            ) : (
                row.Treatmentname
            ),
        },
        {
            name: 'Cost',
            selector: row => parseFloat(row.Cost) || 0,
            sortable: true,
            filterable: true,
            cell: row => isEditing && selectedTreatmentId === row._id ? (
                <input
                    type="number"
                    name="Cost"
                    value={editedTreatment.Cost}
                    onChange={handleInputChange}
                />
            ) : (
                `₱${parseFloat(row.Cost).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
            ),
        },
        // {
        //     name: 'Date Created',
        //     selector: row => new Date(row.createdAt),
        //     sortable: true,
        //     filterable: true,
        //     cell: row => format(new Date(row.updatedAt), 'MM/dd/yyyy hh:mm a')
        // },
        // {
        //     name: 'Date Updated',
        //     selector: row => new Date(row.updatedAt),
        //     sortable: true,
        //     filterable: true,
        //     cell: row => format(new Date(row.updatedAt), 'MM/dd/yyyy hh:mm a')
        // },
        // {
        //     name: 'Actions',
        //     cell: row => (
        //         <div className="action-icons">
        //             {isEditing && selectedTreatmentId === row._id ? (
        //                 <>
        //                     <FaSave className="action-icon" onClick={handleSaveClick} title="Save" />
        //                     <FaTimes className="action-icon" onClick={handleCancelEdit} title="Cancel" />
        //                 </>
        //             ) : (
        //                 <>
        //                     {/* <FaArchive className="action-icon" onClick={() => handleDeleteClick(row._id)} title="Delete" /> */}
        //                     <FaEdit className="action-icon" onClick={() => handleEditClick(row)} title="Edit" />
        //                 </>
        //             )}
        //         </div>
        //     ),
        //     ignoreRowClick: true,
        //     allowOverflow: true,
        //     button: true,
        // }
    ];

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const handleRowsPerPageChange = (newRowsPerPage) => {
        setRowsPerPage(Number(newRowsPerPage));
        setCurrentPage(1);
    };

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1);
    };

    const handleImport = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();

        reader.onload = async (e) => {
            const text = e.target.result;
            const rows = text.split('\n').map(row => row.split(',').map(cell => cell.trim()));

            const [headers, ...dataRows] = rows;
            const data = dataRows
                .filter(row => row.length === headers.length)
                .map(row => {
                    const treatment = {};
                    headers.forEach((header, index) => {
                        treatment[header.trim()] = row[index];
                    });
                    return treatment;
                });

            try {
                const response = await fetch('https://api.hgcdental.clinic/api/treatments/import', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(data),
                });

                const result = await response.json();

                if (!response.ok) {
                    throw new Error(result.message || 'Failed to import data');
                }

                // Safely handle the successful response
                if (Array.isArray(result.insertedTreatments)) {
                    alert(result.message);
                    setTreatments((prevTreatments) => [
                        ...prevTreatments,
                        ...result.insertedTreatments,
                    ]);
                } else {
                    console.warn('Unexpected response format', result);
                }
            } catch (error) {
                console.error('Error importing data:', error);
                alert(error.message || 'Failed to import data. Please try again.');
            }
        };

        reader.readAsText(file);
    };

    const handleExport = () => {
        if (selectedTreatment.length === 0) {
            alert('No Treatment selected for export!');
            return;
        }

        // Define headers for the CSV file
        const csvRows = [
            ['_id', 'Legend', 'Treatmentname', 'Cost', 'createdAt', 'updatedAt', '__v',], // Header row
            ...selectedTreatment.map(treatment => [
                treatment._id || 'null',
                treatment.Legend || 'null',
                treatment.Treatmentname,
                treatment.Cost,
                treatment.createdAt,
                treatment.updatedAt,
                treatment.__v || 0,
            ]),
        ];

        // Convert rows to CSV format
        const csvContent = csvRows.map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);

        // Trigger download
        const link = document.createElement('a');
        link.href = url;
        link.download = 'Treatments.csv';
        link.click();
        URL.revokeObjectURL(url); // Clean up the URL object
    };

    const handleDownloadTemplate = () => {
        // Define headers for the CSV file
        const csvRows = [
            ['_id', 'Legend', 'Treatmentname', 'Cost', 'createdAt', 'updatedAt', '__v',], // Header row
            ...selectedTreatment.map(treatment => [
                '',
                '',
                '',
                '',
                '',
                '',
                '',
            ]),
        ];

        // Convert rows to CSV format
        const csvContent = csvRows.map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);

        // Trigger download
        const link = document.createElement('a');
        link.href = url;
        link.download = 'Treatments.csv';
        link.click();
        URL.revokeObjectURL(url);
    };

    const handleRowSelected = ({ selectedRows }) => {
        setSelectedTreatment(selectedRows);
    };

    // Filter treatments based on search term and view option
    const filteredData = treatments.filter(treatment => {
        // Filter by viewOption
        if (viewOption === 'archived') {
            return treatment.isArchived === true; // Show only archived treatments
        } else {
            return treatment.isArchived !== true; // Hide archived treatments by default
        }
    }).filter(treatment =>
        // Filter by search term
        (treatment.Treatmentname && treatment.Treatmentname.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const paginatedData = filteredData.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    const totalPages = Math.ceil(filteredData.length / rowsPerPage);

    const customStyles = {
        rows: {
            style: {
                borderBottom: '1px solid #ddd', // Thin line per row
            },
        },
        headCells: {
            style: {
                backgroundColor: '#007bff', // Blue background
                color: 'white', // White text
                fontWeight: 'bold', // Make header text bold
                padding: '15px 10px',
                textAlign: 'center', // Center header text
            },
        },
        cells: {
            style: {
                padding: '12px 10px', // Add padding inside the cells
                borderRight: '1px solid #ddd', // Vertical line between columns
            },
        },
        pagination: {
            style: {
                display: 'flex',
                justifyContent: 'center',
                padding: '10px',
                backgroundColor: '#f4f4f4',
            },
        },
        sortIcon: {
            style: {
                color: '#007bff', // Change sort arrow color to blue
                fontSize: '18px', // Increase the size of the sort arrow
            },
        },
    };

    return (
        <div className={`data-table-page ${isSidebarVisible ? 'sidebar-visible' : 'sidebar-hidden'}`}>
            <InternalHeader moduleName="Treatments" toggleSidebar={toggleSidebar} />
            <div className="data-table-container">
                <Sidebar isSidebarVisible={isSidebarVisible} />
                <div className="data-table-container2">
                    <div className="data-table-header">
                        <div className="flex-div">
                            <a href="/create-treatment">
                                <button className="create-button">New</button>
                            </a>
                            <Actions
                                onImportClick={() => {
                                    document.getElementById('file-input').click();
                                }}
                                onDownloadTemplateClick={() => {
                                    handleDownloadTemplate();
                                }}
                            />
                            <input
                                id="file-input"
                                type="file"
                                accept=".csv"
                                style={{ display: 'none' }}
                                onChange={handleImport}
                            />
                        </div>
                        <div>
                            {/* Conditionally render Filter or Actions dropdown */}
                            {selectedTreatment.length > 0 ? (
                                <div className="actions-dropdown-container">
                                    <button onClick={handleActionsToggle} className="actions-button">
                                        <FaCog />  Actions
                                    </button>
                                    {isActionsDropdownOpen && (
                                        <div className="actions-dropdown-options1">
                                            <div onClick={handleExport}>
                                                <FaUpload /> Export Records
                                            </div>
                                            {(viewOption === 'all' || viewOption === 'active') && (
                                                <div onClick={handleDeleteSelected}>
                                                    <FaArchive /> Archive
                                                </div>
                                            )}
                                            {/* Conditionally render the Unarchive button */}
                                            {viewOption === 'archived' && (
                                                <div onClick={handleUnDeleteSelected}>
                                                    <FaArchive /> Unarchive
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <FilterTreatments
                                    searchText={searchTerm}
                                    setSearchText={setSearchTerm}
                                    viewOption={viewOption}
                                    setViewOption={setViewOption}
                                />
                            )}
                        </div>
                        <div className="column-div">
                            <CustomPagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                rowsPerPage={rowsPerPage}
                                onPageChange={handlePageChange}
                                onRowsPerPageChange={handleRowsPerPageChange}
                                totalRecords={filteredData.length}
                            />
                        </div>
                    </div>
                    <div className="table-form">
                        <div>
                            <DataTable
                                title="Treatments"
                                columns={columns}
                                data={paginatedData}
                                highlightOnHover
                                selectableRows
                                selectableRowsHighlight
                                striped
                                onSelectedRowsChange={handleRowSelected}
                                customStyles={customStyles} // Apply custom styles here
                                onRowClicked={handleRowClick}
                            />
                        </div>
                    </div>
                    <div className="log-display-section">
                        <h2>Logs</h2>
                        <div className="log-container">
                            <ul>
                                {logs.map((log, index) => (
                                    <li key={index}>{log.timestamp} - {log.action}: {log.details}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    {showModal && <CreateTreatment onClose={handleCloseModal} addTreatment={addTreatment} />}
                    {showDeleteConfirmation && (
                        <div className="treatments-modal">
                            <div className="modal-overlay">
                                <div className="modal-content">
                                    <h3>Confirm Deletion</h3>
                                    <p>Are you sure you want to delete the treatment <strong>{treatmentToDelete?.Treatmentname}</strong>?</p>
                                    {/* Modal Buttons */}
                                    <div className="modal-buttons">
                                        <button className="modal-button confirm" onClick={confirmDelete}>Yes, Delete</button>
                                        <button className="modal-button cancel" onClick={cancelDelete}>Cancel</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Treatments;